// askSmart-ui/src/pages/TOSP/tospStore.ts
import { useEffect, useState } from "react";

// Local persistence for TOSP while the module has no backend. Every collection
// (buildings, units, tenants) goes through here, so wiring them to real
// endpoints later means replacing this one file rather than each tab.
//
// NB: browser-local and not org-scoped — data lives on this machine only, and
// switching orgs in the same browser shows the same records.
const STORAGE_PREFIX = "tosp:";

export function loadCollection<T>(key: string, fallback: T[]): T[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : fallback;
  } catch {
    // Malformed or unavailable storage (private mode, quota) — fall back to the
    // seed rather than leaving the tab broken.
    return fallback;
  }
}

export function saveCollection<T>(key: string, items: T[]): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(items));
  } catch (err) {
    console.error(`[TOSP] failed to persist "${key}":`, err);
  }
}

// A single stored object rather than a list (e.g. the configuration lists).
// Merged over the seed so a key added later still has a default.
export function loadRecord<T extends object>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? { ...fallback, ...(parsed as T) }
      : fallback;
  } catch {
    return fallback;
  }
}

export function saveRecord<T extends object>(key: string, value: T): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(value));
  } catch (err) {
    console.error(`[TOSP] failed to persist "${key}":`, err);
  }
}

/** The record equivalent of useTospCollection. */
export function useTospRecord<T extends object>(
  key: string,
  seed: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => loadRecord(key, seed));

  useEffect(() => {
    saveRecord(key, value);
  }, [key, value]);

  return [value, setValue];
}

// Drop-in replacement for useState on a TOSP collection: seeded on first use,
// then read back from storage on every later visit and written on every change.
export function useTospCollection<T>(
  key: string,
  seed: T[],
): [T[], React.Dispatch<React.SetStateAction<T[]>>] {
  const [items, setItems] = useState<T[]>(() => loadCollection(key, seed));

  useEffect(() => {
    saveCollection(key, items);
  }, [key, items]);

  return [items, setItems];
}
